import { useEffect, useState } from 'react';
import useOnlineStatus from '@/hooks/useOnlineStatus';

/** Convierte milisegundos en un texto corto: "hace 35 s", "hace 4 min", "hace 2 h". */
function formatElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return 'hace unos segundos';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

/**
 * Aviso flotante y discreto del estado de la conexión.
 * - Solo aparece cuando se pierde el internet (o unos segundos al recuperarlo).
 * - No ocupa espacio en el layout ni bloquea la pantalla.
 * - Se puede cerrar con la X; vuelve a aparecer en la siguiente caída.
 */
export default function ConnectionStatus() {
  const { online, offlineMs, justRecovered } = useOnlineStatus();
  const [dismissed, setDismissed] = useState(false);

  // Al recuperar la conexión se reinicia el descarte para poder mostrar el aviso verde.
  useEffect(() => {
    if (online) setDismissed(false);
  }, [online]);

  const visible = (!online && !dismissed) || justRecovered;
  if (!visible) return null;

  const isOffline = !online;

  return (
    <div className="fixed top-[4.25rem] lg:top-4 inset-x-0 z-[80] flex justify-center px-3 pointer-events-none">
      <div
        role="status"
        aria-live="polite"
        className={`connection-toast pointer-events-auto flex items-center gap-2 rounded-full border px-3 py-1.5 backdrop-blur-md ${
          isOffline
            ? 'border-secondary-300 bg-background-50/95'
            : 'border-accent-300 bg-background-50/95'
        }`}
      >
        {isOffline ? (
          <>
            <span className="relative flex items-center justify-center w-4 h-4 shrink-0">
              <span className="absolute w-2.5 h-2.5 rounded-full bg-secondary-500/40 animate-ping" />
              <span className="relative w-2 h-2 rounded-full bg-secondary-500" />
            </span>
            <i className="ri-wifi-off-line text-sm text-secondary-700" />
            <span className="text-xs font-semibold text-foreground-950 whitespace-nowrap">
              Sin conexión
            </span>
            <span className="text-xs text-foreground-500 whitespace-nowrap">
              {formatElapsed(offlineMs)}
            </span>
            <span className="hidden md:inline text-xs text-foreground-500 whitespace-nowrap">
              · revisa tu internet
            </span>
          </>
        ) : (
          <>
            <span className="flex items-center justify-center w-4 h-4 shrink-0">
              <i className="ri-checkbox-circle-fill text-sm text-accent-600" />
            </span>
            <span className="text-xs font-semibold text-foreground-950 whitespace-nowrap">
              Conexión restablecida
            </span>
            <span className="hidden md:inline text-xs text-foreground-500 whitespace-nowrap">
              · todo sincronizado
            </span>
          </>
        )}

        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Ocultar aviso de conexión"
          className="ml-0.5 flex items-center justify-center w-5 h-5 rounded-full text-foreground-400 hover:text-foreground-800 hover:bg-background-100 transition cursor-pointer"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>
    </div>
  );
}